import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { ThemeMode, getTheme } from '@/themes/themes';
import { useTranslation } from 'react-i18next';

export type Locale = 'en' | 'zh' | 'de';

interface SettingsContextType {
  themeMode: ThemeMode;
  locale: Locale;
  setThemeMode: (mode: ThemeMode) => void;
  setLocale: (locale: Locale) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
}

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const [themeMode, setThemeModeState] = useState<ThemeMode>('light');
  const [locale, setLocaleState] = useState<Locale>('en');

  // Load settings from localStorage on mount (default English if user never switched)
  useEffect(() => {
    console.log('🚀 [Settings] Initializing...');
    const savedTheme = localStorage.getItem('themeMode') as ThemeMode;
    const saved = localStorage.getItem('language') || 'en';
    const savedLocale = (saved === 'en' || saved === 'zh' || saved === 'de' ? saved : 'en') as Locale;
    console.log('📖 [Settings] Read from localStorage:', { theme: savedTheme, locale: savedLocale });

    if (savedTheme) {
      setThemeModeState(savedTheme);
    }

    if (savedLocale) {
      setLocaleState(savedLocale);
      i18n.changeLanguage(savedLocale);
    }
  }, [i18n]);

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem('themeMode', mode);
  };

  const setLocale = async (newLocale: Locale) => {
    try {
      setLocaleState(newLocale);
      localStorage.setItem('language', newLocale);
      // Use i18next to change language without reloading
      await i18n.changeLanguage(newLocale);
      console.log('✅ [Locale Switch] Success:', newLocale);
    } catch (error) {
      console.error('❌ [Locale Switch] Failed:', error);
    }
  };

  const theme = getTheme(themeMode);

  return (
    <SettingsContext.Provider value={{ themeMode, locale, setThemeMode, setLocale }}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </SettingsContext.Provider>
  );
}
